'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// iOS Safari sets navigator.standalone when the app is launched from the home
// screen as a standalone web app. This property is non-standard and missing
// from the lib.dom Navigator interface.
interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean
}

export interface PwaInstallState {
  /** True when PWA native install prompt is available (Android/Chrome) */
  canPromptNative: boolean
  /** True when running on iOS Safari */
  isIOS: boolean
  /** True when app is running in standalone/PWA mode */
  isStandalone: boolean
  /** True when on a mobile device (hides CTA on desktop) */
  isMobile: boolean
  /** Triggers the native install prompt (Android/Chrome only) */
  triggerNativeInstall: () => Promise<boolean>
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false
  // iOS Safari
  if ('standalone' in navigator && (navigator as NavigatorWithStandalone).standalone === true) return true
  // Standard (Chrome, Edge, etc.)
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true
  return false
}

function detectMobile(): boolean {
  if (typeof window === 'undefined') return false
  // Touch capability + small screen
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  const isSmallScreen = window.innerWidth < 1024
  // Also check UA for mobile keywords
  const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  return (hasTouch && isSmallScreen) || mobileUA
}

export function usePwaInstall(): PwaInstallState {
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null)
  const [canPromptNative, setCanPromptNative] = useState(false)
  const [isStandalone, setIsStandalone] = useState(() => detectStandalone())
  const [isIOS] = useState(() => detectIOS())
  const [isMobile] = useState(() => detectMobile())

  useEffect(() => {
    const handleBeforeInstall = (e: Event) => {
      // Prevent the default mini-infobar
      e.preventDefault()
      deferredPromptRef.current = e as BeforeInstallPromptEvent
      setCanPromptNative(true)
    }

    const handleAppInstalled = () => {
      deferredPromptRef.current = null
      setCanPromptNative(false)
      setIsStandalone(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)
    window.addEventListener('appinstalled', handleAppInstalled)

    // Listen for display-mode changes (e.g., user opens from home screen)
    const mql = window.matchMedia('(display-mode: standalone)')
    const handleDisplayChange = (e: MediaQueryListEvent) => {
      setIsStandalone(e.matches)
    }
    mql.addEventListener('change', handleDisplayChange)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled', handleAppInstalled)
      mql.removeEventListener('change', handleDisplayChange)
    }
  }, [])

  const triggerNativeInstall = useCallback(async (): Promise<boolean> => {
    const prompt = deferredPromptRef.current
    if (!prompt) return false

    try {
      await prompt.prompt()
      const { outcome } = await prompt.userChoice
      deferredPromptRef.current = null
      setCanPromptNative(false)
      return outcome === 'accepted'
    } catch {
      return false
    }
  }, [])

  return {
    canPromptNative,
    isIOS,
    isStandalone,
    isMobile,
    triggerNativeInstall,
  }
}
